import { create } from 'zustand'
import {
  MemberType,
  Region,
  EducationalLevel,
  SortBy,
  SortOrder,
} from '@/types/contest'

export const contestSorting: {
  id: string
  value: SortBy
  label: string
  order: 'asc' | 'desc'
}[] = [
  {
    id: 'created_timestamp_desc',
    value: 'created_timestamp',
    label: 'วันประกาศ (มากไปน้อย)',
    order: 'desc',
  },
  {
    id: 'created_timestamp_asc',
    value: 'created_timestamp',
    label: 'วันประกาศ (น้อยไปมาก)',
    order: 'asc',
  },
  {
    id: 'closing_date_desc',
    value: 'closing_date',
    label: 'วันหมดเขตสมัคร (มากไปน้อย)',
    order: 'desc',
  },
  {
    id: 'closing_date_asc',
    value: 'closing_date',
    label: 'วันหมดเขตสมัคร (น้อยไปมาก)',
    order: 'asc',
  },
]

interface ContestPreferenceStore {
  query: string
  setQuery: (query: string) => void
  categories: number[]
  setCategories: (categories: number[]) => void
  educationalLevels: EducationalLevel[]
  setEducationalLevels: (educationalLevels: EducationalLevel[]) => void
  regions: Region[]
  setRegions: (regions: Region[]) => void
  memberTypes: MemberType[]
  setMemberTypes: (memberTypes: MemberType[]) => void
  sortBy: SortBy
  setSortBy: (sortBy: SortBy) => void
  sortOrder: SortOrder
  setSortOrder: (sortOrder: SortOrder) => void
  openDate: Date | null
  setOpenDate: (openDate: Date | null) => void
  closingDate: Date | null
  setClosingDate: (closingDate: Date | null) => void
  teamNumberMin: number | null
  setTeamNumberMin: (teamNumberMin: number | null) => void
  teamNumberMax: number | null
  setTeamNumberMax: (teamNumberMax: number | null) => void
  prizeNumberMin: number | null
  setPrizeNumberMin: (prizeNumberMin: number | null) => void
  prizeNumberMax: number | null
  setPrizeNumberMax: (prizeNumberMax: number | null) => void
  certificate: boolean
  setCertificate: (certificate: boolean) => void
  trophy: boolean
  setTrophy: (trophy: boolean) => void
  internship: boolean
  setInternship: (internship: boolean) => void
  publishWork: boolean
  setPublishWork: (publishWork: boolean) => void
  companyPartnership: boolean
  setCompanyPartnership: (companyPartnership: boolean) => void
  isPrizeSliderActive: boolean
  setIsPrizeSliderActive: (isPrizeSliderActive: boolean) => void
  isBooleanOptionActive: boolean
  setIsBooleanOptionActive: (isBooleanOptionActive: boolean) => void
  isViewActiveContests: boolean
  setIsViewActiveContests: (isViewActiveContests: boolean) => void
  setAll: (state: Partial<ContestPreferenceStore>) => void
  resetAll: () => void
  getState: () => ContestPreferenceStore
}

export const useContestPreferenceStore = create<ContestPreferenceStore>(
  (set, get) => ({
    query: '',
    setQuery: (query: string) => set({ query }),
    categories: [],
    setCategories: (categories: number[]) => set({ categories }),
    educationalLevels: [],
    setEducationalLevels: (educationalLevels: EducationalLevel[]) =>
      set({ educationalLevels }),
    regions: [],
    setRegions: (regions: Region[]) => set({ regions }),
    memberTypes: [],
    setMemberTypes: (memberTypes: MemberType[]) => set({ memberTypes }),
    sortBy: contestSorting[0]?.value ?? 'created_timestamp',
    setSortBy: (sortBy: SortBy) => set({ sortBy }),
    sortOrder: contestSorting[0]?.order ?? 'desc',
    setSortOrder: (sortOrder: SortOrder) => set({ sortOrder }),
    openDate: null,
    setOpenDate: (openDate: Date | null) => set({ openDate }),
    closingDate: null,
    setClosingDate: (closingDate: Date | null) => set({ closingDate }),
    teamNumberMin: null,
    setTeamNumberMin: (teamNumberMin: number | null) => set({ teamNumberMin }),
    teamNumberMax: null,
    setTeamNumberMax: (teamNumberMax: number | null) => set({ teamNumberMax }),
    prizeNumberMin: 0, // TODO null?
    setPrizeNumberMin: (prizeNumberMin: number | null) =>
      set({ prizeNumberMin }),
    prizeNumberMax: 500_000, // TODO null?
    setPrizeNumberMax: (prizeNumberMax: number | null) =>
      set({ prizeNumberMax }),
    certificate: false,
    setCertificate: (certificate: boolean) => set({ certificate }),
    trophy: false,
    setTrophy: (trophy: boolean) => set({ trophy }),
    internship: false,
    setInternship: (internship: boolean) => set({ internship }),
    publishWork: false,
    setPublishWork: (publishWork: boolean) => set({ publishWork }),
    companyPartnership: false,
    setCompanyPartnership: (companyPartnership: boolean) =>
      set({ companyPartnership }),
    isPrizeSliderActive: false,
    setIsPrizeSliderActive: (isPrizeSliderActive: boolean) =>
      set({ isPrizeSliderActive }),
    isBooleanOptionActive: false,
    setIsBooleanOptionActive: (isBooleanOptionActive: boolean) =>
      set({ isBooleanOptionActive }),
    isViewActiveContests: true,
    setIsViewActiveContests: (isViewActiveContests: boolean) =>
      set({ isViewActiveContests }),
    setAll: (state: Partial<ContestPreferenceStore>) => set(state),
    resetAll: () =>
      set({
        query: '',
        categories: [],
        educationalLevels: [],
        regions: [],
        memberTypes: [],
        sortBy: contestSorting[0]?.value ?? 'created_timestamp',
        sortOrder: contestSorting[0]?.order ?? 'desc',
        openDate: null,
        closingDate: null,
        teamNumberMin: null,
        teamNumberMax: null,
        prizeNumberMin: 0, // TODO null?
        prizeNumberMax: 500_000, // TODO null?
        certificate: false,
        trophy: false,
        internship: false,
        publishWork: false,
        companyPartnership: false,
        isPrizeSliderActive: false,
        isBooleanOptionActive: false,
        isViewActiveContests: true,
      }),
    getState: () => get(),
  })
)
